import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Alert, 
  Avatar, 
  Divider, 
  Tooltip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  CircularProgress, 
  Switch, 
  FormControlLabel, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip, 
  Badge,
  Tabs,
  Tab,
  Grid,
  Container
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import api from '../api/api';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneIcon from '@mui/icons-material/Phone';
import TabletIcon from '@mui/icons-material/Tablet';
import SecurityIcon from '@mui/icons-material/Security';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { User, Session } from '../types';
dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to parse user agent and get device info
const parseUserAgent = (userAgent: string): { device: string; browser: string; os: string } => {
  if (!userAgent) return { device: 'Unknown', browser: 'Unknown', os: 'Unknown' };
  
  const ua = userAgent.toLowerCase();
  
  // Detect device type
  let device = 'Desktop';
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    device = 'Mobile';
  } else if (ua.includes('tablet') || ua.includes('ipad')) {
    device = 'Tablet';
  }
  
  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('chrome')) browser = 'Chrome';
  else if (ua.includes('firefox')) browser = 'Firefox';
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
  else if (ua.includes('edge')) browser = 'Edge';
  else if (ua.includes('opera')) browser = 'Opera';
  
  // Detect OS
  let os = 'Unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac')) os = 'macOS';
  else if (ua.includes('linux')) os = 'Linux';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  
  return { device, browser, os };
};

// Helper function to get device icon
const getDeviceIcon = (device: string) => {
  switch (device) {
    case 'Mobile': return <PhoneIcon fontSize="small" />;
    case 'Tablet': return <TabletIcon fontSize="small" />;
    default: return <ComputerIcon fontSize="small" />;
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  };
}

const Profile: React.FC = () => {
  const { user, login } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [editFullName, setEditFullName] = useState(user?.full_name || '');
  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState('');
  const [editError, setEditError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [revokeLoading, setRevokeLoading] = useState<string | null>(null);
  const [revokeAllLoading, setRevokeAllLoading] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [confirmRevokeDialog, setConfirmRevokeDialog] = useState<{ open: boolean; sessionId: number | null; sessionInfo: string }>({ open: false, sessionId: null, sessionInfo: '' });
  const [confirmRevokeAllDialog, setConfirmRevokeAllDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const token = localStorage.getItem('token');
  let currentJti = '';
  if (token) {
    try {
      const decoded: any = jwtDecode(token);
      currentJti = decoded.jti;
    } catch {}
  }

  useEffect(() => {
    setHasChanges(
      editEmail !== (user?.email || '') ||
      editFullName !== (user?.full_name || '')
    );
  }, [editEmail, editFullName, user]);

  const fetchSessions = async () => {
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const res = await api.get('/users/me/sessions');
      setSessions(res.data as Session[]);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to load sessions';
      setSessionsError(errorMessage);
      showError(errorMessage);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevoke = async (sessionId: number) => {
    setRevokeLoading(String(sessionId));
    try {
      await api.delete(`/users/me/sessions/${sessionId}`);
      await fetchSessions();
      showSuccess('Session revoked successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to revoke session';
      showError(errorMessage);
    } finally {
      setRevokeLoading(null);
    }
  };

  const handleRevokeAll = async () => {
    setRevokeAllLoading(true);
    try {
      await api.delete('/users/me/sessions');
      await fetchSessions();
      showSuccess('All other sessions revoked successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to revoke all sessions';
      showError(errorMessage);
    } finally {
      setRevokeAllLoading(false);
    }
  };

  const openRevokeDialog = (session: Session) => {
    const deviceInfo = parseUserAgent(session.user_agent || '');
    const sessionInfo = `${deviceInfo.device} (${deviceInfo.browser} on ${deviceInfo.os}) - ${session.ip_address || 'Unknown IP'}`;
    setConfirmRevokeDialog({ open: true, sessionId: session.id, sessionInfo });
  };

  const openRevokeAllDialog = () => {
    setConfirmRevokeAllDialog(true);
  };

  const closeRevokeDialog = () => {
    setConfirmRevokeDialog({ open: false, sessionId: null, sessionInfo: '' });
  };

  const closeRevokeAllDialog = () => {
    setConfirmRevokeAllDialog(false);
  };

  const confirmRevoke = () => {
    if (confirmRevokeDialog.sessionId) {
      handleRevoke(confirmRevokeDialog.sessionId);
    }
    closeRevokeDialog();
  };

  const confirmRevokeAll = () => {
    handleRevokeAll();
    closeRevokeAllDialog();
  };

  const activeSessionsCount = sessions.filter(s => s.is_active).length;
  const currentSession = sessions.find(s => s.token_jti === currentJti);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
        setError("New password must be at least 8 characters long.");
        return;
    }

    try {
      await api.put('/users/me/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      showSuccess('Password updated successfully!');
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred while updating the password.');
      showError('Failed to update password.');
    }
  };

  const handleProfileSave = async () => {
    if (!user) return;
    setEditLoading(true);
    setEditSuccess('');
    setEditError('');
    try {
      await api.patch(`/users/${user.id}`, {
        email: editEmail,
        full_name: editFullName,
      });
      // Fetch updated user info and update AuthContext
      const res = await api.get('/users/me');
      login(res.data as any);
      setEditSuccess('Profile updated successfully!');
      showSuccess('Profile updated successfully!');
    } catch (err: any) {
      setEditError(err.response?.data?.detail || 'Failed to update profile.');
      showError('Failed to update profile.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header Section */}
      <Box sx={{ 
        mb: 4, 
        textAlign: 'center',
        p: 2
      }}>
        <Typography variant="h3" gutterBottom sx={{ 
          fontWeight: 700, 
          color: '#2c3e50',
          mb: 2
        }}>
        Profile Management
      </Typography>
        <Typography variant="subtitle1" sx={{ 
          color: '#7f8c8d',
          fontWeight: 400
        }}>
          Manage your account settings and security preferences
        </Typography>
      </Box>

      {/* User Profile Card */}
      {user && (
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', 
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
            borderRadius: '50%',
            zIndex: 0
          }
        }}>
          <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar 
                  sx={{ 
                    width: 90, 
                    height: 90, 
                    bgcolor: 'rgba(52, 152, 219, 0.2)', 
                    fontSize: '2.5rem',
                    border: '4px solid rgba(52, 152, 219, 0.3)',
                    boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
                    }
                  }}
                >
                  {user.full_name ? user.full_name[0].toUpperCase() : <PersonIcon fontSize="large" />}
            </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: 'white'
                }}>
              {user.full_name || user.username}
            </Typography>
                <Typography variant="subtitle1" sx={{ 
                  opacity: 0.9, 
                  mb: 1,
                  fontWeight: 500,
                  color: '#bdc3c7'
                }}>
              {user.role}
              {(user.school_name || (user as any).school?.name) ? ` at ${user.school_name || (user as any).school?.name}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ 
                  opacity: 0.8,
                  color: '#ecf0f1',
                  fontWeight: 400
                }}>
                  Member since {user.created_at ? dayjs.utc(user.created_at).tz('Africa/Nairobi').format('MMMM YYYY') : 'Unknown'}
                </Typography>
              </Grid>
              <Grid item>
                <Chip 
                  label={user.is_active ? 'Active Account' : 'Inactive'} 
                  color={user.is_active ? 'success' : 'default'}
                  sx={{ 
                    bgcolor: user.is_active ? 'rgba(39, 174, 96, 0.2)' : 'rgba(231, 76, 60, 0.2)', 
                    color: user.is_active ? '#2ecc71' : '#e74c3c',
                    border: `2px solid ${user.is_active ? '#2ecc71' : '#e74c3c'}`,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: user.is_active ? 'rgba(39, 174, 96, 0.3)' : 'rgba(231, 76, 60, 0.3)',
                    }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="profile tabs"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 500,
            }
          }}
        >
          <Tab 
            icon={<AccountCircleIcon />} 
            label="Account Details" 
            {...a11yProps(0)} 
            iconPosition="start"
          />
          <Tab 
            icon={<SecurityIcon />} 
            label="Security" 
            {...a11yProps(1)} 
            iconPosition="start"
          />
          <Tab 
            icon={<DevicesIcon />} 
            label="Active Sessions" 
            {...a11yProps(2)} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e1e8ed',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: 'white',
                mr: 2
              }}>
                <EditIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Profile Information
            </Typography>
          </Box>
            
            <Box display="flex" flexDirection="column" gap={2.5}>
            <Tooltip title="Your login email address" arrow>
              <TextField
                label="Email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                required
                fullWidth
                size="small"
                type="email"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db',
                      },
                    }
                  }}
              />
            </Tooltip>
            <Tooltip title="Your full name as displayed to others" arrow>
              <TextField
                label="Full Name"
                value={editFullName}
                onChange={e => setEditFullName(e.target.value)}
                required
                fullWidth
                size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db',
                      },
                    }
                  }}
              />
            </Tooltip>
          </Box>
            
            {editError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {editError}
              </Alert>
            )}
            {editSuccess && (
              <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                {editSuccess}
              </Alert>
            )}
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button 
                variant="contained" 
                onClick={handleProfileSave} 
                disabled={editLoading || !hasChanges} 
                size="large"
                startIcon={editLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ 
                  minWidth: 150,
                  background: hasChanges ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' : '#bdc3c7',
                  '&:hover': {
                    background: hasChanges ? 'linear-gradient(135deg, #2980b9 0%, #1f5f8b 100%)' : '#bdc3c7',
                    transform: hasChanges ? 'translateY(-1px)' : 'none',
                    boxShadow: hasChanges ? '0 4px 12px rgba(52, 152, 219, 0.3)' : 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          mt: 3,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e1e8ed',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)',
                color: 'white',
                mr: 2
              }}>
                <AccountCircleIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Account Details
              </Typography>
            </Box>
            
            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField 
                label="Username" 
                value={user?.username} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
              <TextField 
                label="Role" 
                value={user?.role} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
              <TextField 
                label="Account Status" 
                value={user?.is_active === false ? 'Inactive' : 'Active'} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
              <TextField 
                label="School ID" 
                value={user?.school_id ?? '-'} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
              {(user?.school_name || (user as any)?.school?.name) && (
                <TextField 
                  label="School Name" 
                  value={user?.school_name || (user as any)?.school?.name} 
                  InputProps={{ readOnly: true }} 
                  fullWidth 
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: '#e1e8ed',
                      }
                    }
                  }}
                />
              )}
              <TextField 
                label="Created At" 
                value={user?.created_at ? dayjs.utc(user.created_at).tz('Africa/Nairobi').format('YYYY-MM-DD HH:mm:ss') : '-'} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
              <TextField 
                label="Last Login" 
                value={user?.last_login ? dayjs.utc(user.last_login).tz('Africa/Nairobi').format('YYYY-MM-DD HH:mm:ss') : '-'} 
                InputProps={{ readOnly: true }} 
                fullWidth 
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e1e8ed',
                    }
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e1e8ed',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(231, 76, 60, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: 'white',
                mr: 2
              }}>
                <SecurityIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Change Password
              </Typography>
            </Box>
            
          <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                fullWidth
                size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e74c3c',
                      },
                    }
                  }}
              />
              <TextField
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                size="small"
                helperText="At least 8 characters."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e74c3c',
                      },
                    }
                  }}
              />
              <TextField
                type="password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e74c3c',
                      },
                    }
                  }}
              />
              {error && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    size="large"
                    sx={{ 
                      minWidth: 200,
                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #c0392b 0%, #a93226 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Update Password
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>

        <Card sx={{ 
          mt: 3,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e1e8ed',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #f39c12 0%, #d68910 100%)',
                color: 'white',
                mr: 2
              }}>
                <SecurityIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Security Tips
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  backgroundColor: '#3498db', 
                  mt: 1, 
                  flexShrink: 0 
                }} />
                <Typography variant="body2" color="text.secondary">
                  Use a strong password with at least 8 characters
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  backgroundColor: '#3498db', 
                  mt: 1, 
                  flexShrink: 0 
                }} />
                <Typography variant="body2" color="text.secondary">
                  Include uppercase, lowercase, numbers, and symbols
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  backgroundColor: '#3498db', 
                  mt: 1, 
                  flexShrink: 0 
                }} />
                <Typography variant="body2" color="text.secondary">
                  Avoid using personal information in your password
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  backgroundColor: '#3498db', 
                  mt: 1, 
                  flexShrink: 0 
                }} />
                <Typography variant="body2" color="text.secondary">
                  Regularly review your active sessions
                </Typography>
              </Box>
            </Box>
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e1e8ed',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                  color: 'white',
                  mr: 2
                }}>
                  <DevicesIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Active Sessions
                </Typography>
                <Badge badgeContent={activeSessionsCount} color="primary" sx={{ ml: 2 }}>
                  <Chip 
                    label={`${activeSessionsCount} Active`} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                    sx={{ 
                      borderColor: '#3498db',
                      color: '#3498db',
                      fontWeight: 500
                    }}
                  />
                </Badge>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Refresh sessions">
                  <IconButton 
                    onClick={fetchSessions} 
                    disabled={sessionsLoading} 
                    size="small"
                    sx={{
                      color: '#3498db',
                      '&:hover': {
                        backgroundColor: 'rgba(52, 152, 219, 0.1)'
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <FormControlLabel
                  control={<Switch checked={showRevoked} onChange={e => setShowRevoked(e.target.checked)} color="primary" />}
                  label="Show revoked sessions"
                  sx={{ ml: 1 }}
                />
              </Box>
            </Box>
            
            {sessionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : sessionsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {sessionsError}
                <Button size="small" onClick={fetchSessions} sx={{ ml: 2 }}>
                  Try Again
                </Button>
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid #e1e8ed',
                  '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                  }
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ 
                        backgroundColor: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                        '& .MuiTableCell-head': {
                          backgroundColor: '#3498db',
                          color: 'white',
                          fontWeight: 600,
                          borderBottom: 'none',
                          '&:first-of-type': {
                            borderTopLeftRadius: 8,
                          },
                          '&:last-of-type': {
                            borderTopRightRadius: 8,
                          }
                        }
                      }}>
                        <TableCell>Device</TableCell>
                        <TableCell>Browser & OS</TableCell>
                        <TableCell>IP Address</TableCell>
                        <TableCell>Issued</TableCell>
                        <TableCell>Last Active</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(showRevoked ? sessions : sessions.filter(s => s.is_active)).map((session) => {
                        const deviceInfo = parseUserAgent(session.user_agent || '');
                        const isCurrentSession = session.token_jti === currentJti;
                        
                        return (
                          <TableRow key={session.id} selected={isCurrentSession}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getDeviceIcon(deviceInfo.device)}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {deviceInfo.device}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {deviceInfo.browser} on {deviceInfo.os}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {session.ip_address || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {session.issued_at ? dayjs.utc(session.issued_at).tz('Africa/Nairobi').format('MMM DD, YYYY HH:mm') : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {session.last_active ? dayjs.utc(session.last_active).tz('Africa/Nairobi').format('MMM DD, YYYY HH:mm') : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={session.is_active ? (isCurrentSession ? 'Current' : 'Active') : 'Revoked'} 
                                color={session.is_active ? (isCurrentSession ? 'primary' : 'success') : 'default'}
                                size="small"
                                variant={isCurrentSession ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {!isCurrentSession && session.is_active && (
                                <Tooltip title="Revoke this session">
                                  <span>
                                    <IconButton 
                                      onClick={() => openRevokeDialog(session)} 
                                      disabled={revokeLoading === String(session.id)} 
                                      size="small"
                                      color="error"
                                    >
                                      <ExitToAppIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {isCurrentSession && session.is_active && (
                                <Tooltip title="This is your current session">
                                  <span>
                                    <IconButton disabled size="small" color="primary">
                                      <ExitToAppIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {activeSessionsCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {activeSessionsCount - 1} other active session{activeSessionsCount - 1 !== 1 ? 's' : ''}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      onClick={openRevokeAllDialog} 
                      disabled={revokeAllLoading}
                      startIcon={<ExitToAppIcon />}
                    >
                      {revokeAllLoading ? 'Revoking...' : 'Revoke All Other Sessions'}
                    </Button>
                  </Box>
                )}
                
                {sessions.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No sessions found
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Dialogs */}
      <Dialog open={confirmRevokeDialog.open} onClose={closeRevokeDialog}>
        <DialogTitle>Confirm Revoke Session</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to revoke the session for:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>{confirmRevokeDialog.sessionInfo}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeDialog} color="primary">Cancel</Button>
          <Button onClick={confirmRevoke} color="error" variant="contained" disabled={revokeLoading === String(confirmRevokeDialog.sessionId)}>
            {revokeLoading === String(confirmRevokeDialog.sessionId) ? 'Revoking...' : 'Revoke Session'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmRevokeAllDialog} onClose={closeRevokeAllDialog}>
        <DialogTitle>Confirm Revoke All Sessions</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to revoke all other sessions for this account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeAllDialog} color="primary">Cancel</Button>
          <Button onClick={confirmRevokeAll} color="error" variant="contained" disabled={revokeAllLoading}>
            {revokeAllLoading ? 'Revoking...' : 'Revoke All Sessions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile; 